import { DOCUMENT, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn } from '@angular/router';

/** Built by angular.json as separate, non-injected style bundles. */
export const APP_STYLES_HREF = 'app-styles.css';
export const DESK_STYLES_HREF = 'desk-styles.css';

const loaded = new Map<string, Promise<void>>();

/**
 * The public pages only ship the Talespinner design tokens and page styles. The dashboard, account and
 * reader screens also need Bootstrap, the admin theme and the icon fonts (~800 kB of CSS), so those are
 * fetched the first time one of these routes is entered, before it renders (no unstyled flash).
 */
export const appStylesGuard: CanActivateFn = () => stylesGuard(APP_STYLES_HREF);

/** Desk has its own small bundle instead of Bootstrap and the admin theme (TD §17.2). */
export const deskStylesGuard: CanActivateFn = () => stylesGuard(DESK_STYLES_HREF);

function stylesGuard(href: string): true | Promise<true> {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) {
    return true;
  }
  const document = inject(DOCUMENT);
  if (!loaded.has(href)) {
    loaded.set(href, loadStylesheet(document, href));
  }
  return loaded.get(href)!.then(() => true);
}

function loadStylesheet(document: Document, href: string): Promise<void> {
  return new Promise(resolve => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    // A failed stylesheet should not lock anyone out of the app; it just renders unstyled.
    link.onload = () => resolve();
    link.onerror = () => resolve();

    // Right after the global stylesheet, so the theme keeps overriding the tokens and component styles
    // keep overriding the theme, as when everything was one bundle.
    const globalStyles = document.head.querySelector('link[rel="stylesheet"][href^="styles"]');
    if (globalStyles) {
      globalStyles.after(link);
    } else {
      document.head.prepend(link);
    }
  });
}

/**
 * Turns a loaded bundle off or back on. Desk turns the app bundle off while it is open, so Bootstrap
 * from an earlier dashboard visit doesn't restyle it. This goes through `media`, not `disabled`: a
 * disabled link drops its sheet, so turning it back on fetched and parsed the bundle again, and the
 * dashboard rendered (and laid out its sidebar) unstyled in the meantime.
 */
export function setStylesheetEnabled(document: Document, href: string, enabled: boolean): void {
  const link = document.head.querySelector<HTMLLinkElement>(`link[rel="stylesheet"][href="${href}"]`);
  if (!link) {
    return;
  }
  if (enabled) {
    link.removeAttribute('media');
  } else {
    link.media = 'not all';
  }
}
